/**
 * Permission atoms and default role specifications.
 *
 * See AGENT.md §8 for usage. To add a new permission:
 *   1. Add the atom here
 *   2. Add it to default roles below (least privilege)
 *   3. Write a migration to backfill existing system roles
 *   4. Add @RequirePermission to relevant endpoints
 *
 * Never check role names directly (`if (user.role === 'Designer')`).
 * Always check permissions via `PermissionService.require(actor, perm, teamId)`.
 */

export const Permission = {
  // Design
  CAN_DESIGN: 'can_design',
  CAN_VIEW_GOLD: 'can_view_gold',

  // Recording
  CAN_RECORD: 'can_record',
  CAN_REJECT_RECORDING: 'can_reject_recording',
  CAN_IMPORT_AUDIO: 'can_import_audio',
  /** Project-level: record the English question audio across assigned languoids. */
  CAN_RECORD_QUESTIONS: 'can_record_questions',

  // Testing
  CAN_RUN_TEST: 'can_run_test',

  // Judgement
  CAN_JUDGE: 'can_judge',
  CAN_APPROVE: 'can_approve',
  CAN_TIEBREAK: 'can_tiebreak',

  // Workflow
  CAN_ASSIGN: 'can_assign',
  CAN_FORCE_REWORK: 'can_force_rework',
  CAN_EXPORT: 'can_export',

  // Dashboard / data
  CAN_VIEW_DASHBOARD: 'can_view_dashboard',
  CAN_VIEW_AUDIT_LOG: 'can_view_audit_log',

  // Team admin
  CAN_MANAGE_TEAM: 'can_manage_team',
  CAN_MANAGE_ROLES: 'can_manage_roles',
  CAN_CONFIGURE_GEMINI: 'can_configure_gemini',
  CAN_CONFIGURE_SHEETS_SYNC: 'can_configure_sheets_sync',
  CAN_IMPORT_CSV: 'can_import_csv',

  // Project admin
  CAN_VIEW_PROJECT_REPORTS: 'can_view_project_reports',
  CAN_MANAGE_PROJECT: 'can_manage_project',
} as const;

export type PermissionAtom = (typeof Permission)[keyof typeof Permission];

export const ALL_PERMISSIONS: readonly PermissionAtom[] = Object.values(
  Permission,
) as readonly PermissionAtom[];

/**
 * Permissions that only make sense at the project level (held via an ProjectMembership
 * and resolved with `resolveProject`). A role granting any of these is an project role —
 * it should be assigned to project-scoped users, not offered as a team-member role.
 */
export const PROJECT_PERMISSIONS: readonly PermissionAtom[] = [
  Permission.CAN_MANAGE_PROJECT,
  Permission.CAN_VIEW_PROJECT_REPORTS,
  Permission.CAN_RECORD_QUESTIONS,
];

/** True if a role's permission set makes it project-scoped (vs team-scoped). */
export const isProjectScopedRole = (permissions: readonly string[]): boolean =>
  PROJECT_PERMISSIONS.some((p) => permissions.includes(p));

/**
 * Default system roles. Team Admins can clone any of these and customize.
 * These are the ground truth for first-time team setup.
 */
export const SYSTEM_ROLES = {
  DESIGNER: {
    name: 'Designer',
    permissions: [
      Permission.CAN_DESIGN,
      Permission.CAN_VIEW_GOLD,
      Permission.CAN_RUN_TEST,
      Permission.CAN_JUDGE,
      Permission.CAN_VIEW_DASHBOARD,
    ],
  },
  RECORDER: {
    name: 'Recorder',
    permissions: [Permission.CAN_RECORD, Permission.CAN_IMPORT_AUDIO],
  },
  QC_REVIEWER: {
    name: 'QC Reviewer',
    permissions: [
      Permission.CAN_VIEW_GOLD,
      Permission.CAN_JUDGE,
      Permission.CAN_APPROVE,
      Permission.CAN_REJECT_RECORDING,
      Permission.CAN_FORCE_REWORK,
      Permission.CAN_TIEBREAK,
      Permission.CAN_VIEW_DASHBOARD,
      Permission.CAN_VIEW_AUDIT_LOG,
    ],
  },
  OBSERVER: {
    // Project-level read-only role: watches overall progress across the project on both
    // the desktop and web dashboards (no editing).
    name: 'Observer',
    permissions: [Permission.CAN_VIEW_DASHBOARD, Permission.CAN_VIEW_PROJECT_REPORTS],
  },
  QUESTION_RECORDER: {
    // Project-level role held via an ProjectMembership: records the English question
    // audio for challenges across the languoids assigned on the membership.
    name: 'Question Recorder',
    permissions: [Permission.CAN_RECORD_QUESTIONS],
  },
  TEAM_ADMIN: {
    name: 'Team Admin',
    permissions: [
      // Everything except project-level
      Permission.CAN_DESIGN,
      Permission.CAN_VIEW_GOLD,
      Permission.CAN_RECORD,
      Permission.CAN_REJECT_RECORDING,
      Permission.CAN_IMPORT_AUDIO,
      Permission.CAN_RUN_TEST,
      Permission.CAN_JUDGE,
      Permission.CAN_APPROVE,
      Permission.CAN_TIEBREAK,
      Permission.CAN_ASSIGN,
      Permission.CAN_FORCE_REWORK,
      Permission.CAN_EXPORT,
      Permission.CAN_VIEW_DASHBOARD,
      Permission.CAN_VIEW_AUDIT_LOG,
      Permission.CAN_MANAGE_TEAM,
      Permission.CAN_MANAGE_ROLES,
      Permission.CAN_CONFIGURE_GEMINI,
      Permission.CAN_CONFIGURE_SHEETS_SYNC,
      Permission.CAN_IMPORT_CSV,
    ],
  },
  ADMIN: {
    // Project-level operator (held via a ProjectMembership). The desktop is the
    // operator console, and the Project Admin can do anything on it: the whole
    // Configuration console (project, teams, users, roles, model/sheets config),
    // the Dashboard, the Test Runner, and Export — across every team in the
    // project (its project-level perms resolve inside each team, see
    // PermissionService.resolve). It is NOT a content author/judge/recorder
    // (those live on web/mobile), so it omits design/judge/record perms.
    name: 'Admin',
    permissions: [
      // Project administration
      Permission.CAN_MANAGE_PROJECT,
      Permission.CAN_MANAGE_ROLES,
      Permission.CAN_CONFIGURE_GEMINI,
      Permission.CAN_CONFIGURE_SHEETS_SYNC,
      // Visibility (dashboards, audit, gold)
      Permission.CAN_VIEW_PROJECT_REPORTS,
      Permission.CAN_VIEW_DASHBOARD,
      Permission.CAN_VIEW_AUDIT_LOG,
      Permission.CAN_VIEW_GOLD,
      // Operate (Test Runner + Export)
      Permission.CAN_RUN_TEST,
      Permission.CAN_REJECT_RECORDING,
      Permission.CAN_FORCE_REWORK,
      Permission.CAN_EXPORT,
    ],
  },
} as const;

/**
 * Effective permissions for a given role + custom overrides.
 * Custom permissions are union-added; there is no subtraction primitive.
 */
export const resolvePermissions = (
  rolePermissions: readonly PermissionAtom[] | undefined,
  customPermissions: readonly PermissionAtom[] = [],
): Set<PermissionAtom> => {
  const set = new Set<PermissionAtom>(rolePermissions ?? []);
  for (const p of customPermissions) set.add(p);
  return set;
};

export const hasPermission = (
  effective: Set<PermissionAtom>,
  required: PermissionAtom,
): boolean => effective.has(required);
